import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/firebase";
import { getUserProfile, login, logout, signUp } from "../services/authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  const loadProfile = async (firebaseUser, forceTokenRefresh = false) => {
    if (!firebaseUser) {
      setProfile(null);
      setProfileLoading(false);
      return null;
    }

    setProfileLoading(true);
    try {
      if (forceTokenRefresh) {
        await firebaseUser.getIdToken(true);
      }
      const p = await getUserProfile(firebaseUser.uid);
      setProfile(p);
      return p;
    } catch {
      setProfile(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await loadProfile(firebaseUser, true);
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const refreshOnFocus = async () => {
      if (!auth.currentUser) return;
      await loadProfile(auth.currentUser, true);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshOnFocus();
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      profileLoading,
      signUpUser: async (email, password, displayName, accessCode) => signUp(email, password, displayName, accessCode),
      loginUser: async (email, password) => {
        setProfileLoading(true);
        const result = await login(email, password);
        setProfile(result.profile);
        setProfileLoading(false);
        return result;
      },
      signOutUser: logout,
      refreshProfile: async () => {
        if (!auth.currentUser) return null;
        return loadProfile(auth.currentUser, true);
      },
    }),
    [loading, profile, profileLoading, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
